// The call flow's pure model (CU-869fmc59c). No React, MUI or redux, so Jest loads
// it without the components barrel.
//
// One host (CallFlowProvider) runs three screens for any call a staff member
// points at: the outcome screen, Create lead and Link to existing. The call can
// come from three places with three shapes, so everything is turned into one
// "flow call" here first:
//   - a Call Logs row (callLog.normalize.js, id like `voice:9`)
//   - a To call back queue row (queue/queueModel.js, carries `callRow`)
//   - a live pill call (callPill/callPillModel.js, carries `callId`)
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { otherParty, lineDisplay, pillLeadRow } from '../callPill/callPillModel';
import { FOLLOW_UP_STATE } from '../../pages/dashboard/callLogs/callLog.constants';
import { FOLLOW_UP_OUTCOMES, callBackNumber, dialableNumber, outcomeOptionsFor } from '../../pages/dashboard/callLogs/rowActions';
import { displayNameIsLine, lineText } from '../../pages/dashboard/callLogs/caller/callerModel';
import { prefillFields, withCallFallbacks } from '../../pages/dashboard/callLogs/caller/leadPrefill';
import { formatUkPhone } from '../../utils/ukPhone';
import { cleanLineLabel } from '../../utils/callLineLabel';

dayjs.extend(utc);
dayjs.extend(timezone);

const UK = 'Europe/London';

// ── Events (non-React callers, e.g. UX-A's row menu) ─────────────────────────
// window.dispatchEvent(new CustomEvent('call-flow:open', { detail: { action, call } }))
export const CALL_FLOW_EVENT = 'call-flow:open';
// The flow changed something a list shows (outcome saved, lead created, call linked).
export const CALL_FLOW_CHANGED_EVENT = 'call-flow:changed';
// The Create lead wizard saved a lead for a call: { leadId, call }.
export const CALL_FLOW_LEAD_CREATED_EVENT = 'call-flow:lead-created';
// The caller's dialer was started for a row: { call } (the host then waits for the call to end).
export const CALL_FLOW_AWAIT_END_EVENT = 'call-flow:await-end';
// "Did they answer?" for a call we can't read the result of: { call, canNotAPatient }. The dialer
// widget (which hosts the question in its finished state) cancels the event to say it took it.
export const CALL_FLOW_ASK_EVENT = 'call-flow:ask';

// The Twilio widget's "call finished" window event. Same string as TWILIO_VOICE_CALL_ENDED_EVENT in
// sections/twilioVoice/TwilioVoiceProvider.jsx, kept here so this model doesn't import the widget.
export const TWILIO_CALL_ENDED_EVENT = 'twilio-voice-call-finished';
// A call-back the host waits on stops counting after this long.
export const AWAIT_END_TTL_MS = 30 * 60 * 1000;

// The key under which the wizard's step-1 form is kept in sessionStorage
// (hooks/useFormStatePreservation: formState:<dialog>:<record>). Cleared before a call-prefilled
// open so the prefill wins over whatever an earlier abandoned form left behind.
export const CREATE_LEAD_FORM_STATE_KEY = 'formState:personal-details:new';

export const CALL_FLOW_ACTION = Object.freeze({
    OUTCOME: 'outcome',
    CREATE_LEAD: 'createLead',
    LINK_LEAD: 'linkLead',
    CALL_BACK: 'callBack',
});

// Tiny dispatcher for non-React callers: requestCallFlow('outcome' | 'createLead' | 'linkLead' | 'callBack', call, options).
export const requestCallFlow = (action, call, options = {}) => {
    if (typeof window === 'undefined' || !call) return;
    window.dispatchEvent(new CustomEvent(CALL_FLOW_EVENT, { detail: { action, call, options } }));
};
export const announceCallFlowChange = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(CALL_FLOW_CHANGED_EVENT));
};

// ── The three outcome cards of the existing follow-up step ──────────────────
// Same keys FollowUp.jsx stores in redux (state.lead.BookingAttendance).
export const CARD = Object.freeze({
    BOOKED: 'Booking Details',
    SPOKE: 'Appointment Not Booked',
    NO_ANSWER: 'Unable to contact',
});

// The question the dialer widget asks after a call we can't read the result of
// ("Did they answer?"): what the staff member says happened.
export const ASK_CHOICE = Object.freeze({
    SPOKE: 'spoke',
    NO_ANSWER: 'no_answer',
    NOT_A_PATIENT: 'not_a_patient',
});

// The buttons of that question, in order. Not a patient only where the flow allows it today.
export const askChoices = ({ canNotAPatient = false } = {}) => [
    { id: ASK_CHOICE.SPOKE, label: 'Spoke', primary: true },
    { id: ASK_CHOICE.NO_ANSWER, label: 'No answer', primary: false },
    ...(canNotAPatient ? [{ id: ASK_CHOICE.NOT_A_PATIENT, label: 'Not a patient', primary: false }] : []),
];

// Card → `POST calls/{id}/outcome/` outcome (contract: booked | spoke | no_answer).
export const outcomeForCard = (card) => ({
    [CARD.BOOKED]: 'booked',
    [CARD.SPOKE]: 'spoke',
    [CARD.NO_ANSWER]: 'no_answer',
}[card] || null);

// Card → `POST calls/{id}/call-outcome/` (the user's own outbound call). No answer
// writes nothing: the backend already logged the rang-out call.
export const ownOutcomeForCard = (card, { newLead = false } = {}) => {
    if (card === CARD.BOOKED || card === CARD.SPOKE) return newLead ? 'spoke_new_lead' : 'spoke_existing_patient';
    return null;
};

// ── Normalising a call ───────────────────────────────────────────────────────
const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');
const isPillCall = (c) => c.callId !== undefined && c.callId !== null && c.id === undefined;
const leadOf = (lead) => (lead && lead.id != null
    ? { id: lead.id, name: str(lead.name) || `Lead ${lead.id}`, isArchived: lead.isArchived === true || lead.is_archived === true }
    : null);

const fromRow = (row, extra = {}) => {
    const lead = leadOf(row.lead || extra.lead);
    // The backend owes a call-back for missed, voicemail, busy and failed inbound calls (FOLLOW_UP_OUTCOMES), but
    // Call Logs rows only carry a follow-up state for missed ones: for the others the server's own next actions
    // (a call-back / log-outcome button that is enabled) say it is still open.
    const serverOpen = Array.isArray(row.nextActions) && row.nextActions.some((a) => (typeof a === 'string'
        ? ['call_back', 'log_outcome'].includes(a)
        : ['call_back', 'log_outcome'].includes(a?.id || a?.key) && a.enabled !== false && a.disabled !== true));
    const open = Boolean(FOLLOW_UP_STATE[row.followUpState]?.open);
    const owed = row.direction === 'inbound' && FOLLOW_UP_OUTCOMES.includes(row.outcome) && (open || (row.outcome !== 'missed' && serverOpen));
    return {
        source: extra.source || 'row',
        id: String(row.id),
        number: dialableNumber({ contact: str(extra.number) }) || callBackNumber(row) || dialableNumber(row),
        lead,
        line: lineText(row),
        summary: str(row.aiReview?.summary),
        timestamp: row.timestamp || null,
        timestampHuman: str(row.timestampHuman),
        direction: row.direction || '',
        outcome: row.outcome || '',
        followUpState: row.followUpState || '',
        callerLabel: row.callerLabel || '',
        isWhatsapp: String(row.id).startsWith('whatsapp:'),
        owed,
        ownOutbound: false,
        pillCallId: null,
        row,
    };
};

// A call the widget placed that has no Call Logs row yet (a fresh dial, nobody owed a call-back):
// enough to ask "Did they answer?" and to create a lead from the number. It has no id the backend
// knows, so nothing is written or linked for it (`synthetic`).
export const dialedCall = ({ number = '', leadId = null, leadName = '' } = {}) => {
    const lead = leadOf(leadId != null ? { id: leadId, name: leadName } : null);
    return {
        source: 'dialed',
        id: `dialed:${number || 'unknown'}`,
        number,
        lead,
        line: '',
        summary: '',
        timestamp: null,
        timestampHuman: '',
        direction: 'outbound',
        outcome: '',
        followUpState: '',
        callerLabel: '',
        isWhatsapp: false,
        owed: false,
        ownOutbound: false,
        pillCallId: null,
        synthetic: true,
        row: { id: `dialed:${number || 'unknown'}`, direction: 'outbound', lead },
    };
};

const fromPill = (c) => {
    const row = {
        ...pillLeadRow(c),
        // Earlier calls are linked only when the caller label rules out a shared number.
        linkEarlier: Boolean(c.callerLabel) && c.callerLabel !== 'shared_number',
    };
    return {
        source: 'pill',
        id: row.id,
        number: c.callerIdWithheld ? '' : dialableNumber({ contact: otherParty(c) }),
        lead: leadOf(c.lead),
        line: lineDisplay(c),
        summary: '',
        timestamp: c.startedAt || null,
        timestampHuman: row.timestampHuman,
        direction: c.direction || '',
        outcome: c.outcome || '',
        followUpState: '',
        callerLabel: c.callerLabel || '',
        isWhatsapp: false,
        owed: false,
        ownOutbound: Boolean(c.mine && c.direction === 'outbound'),
        pillCallId: c.callId,
        row,
    };
};

// Any of the three shapes (or an already normalised flow call) → one flow call; null when unusable.
export function normaliseCall(input) {
    if (!input || typeof input !== 'object') return null;
    if (input.source && input.row && input.id) return input;
    if (input.callRow && input.callRow.id) return fromRow(input.callRow, { source: 'queue', number: input.number, lead: input.lead });
    if (isPillCall(input)) return fromPill(input);
    if (input.id) return fromRow(input);
    return null;
}

// A flow call with the lead the call has just been linked to (or created).
export const withLead = (call, lead) => ({
    ...call,
    lead: leadOf(lead),
    row: { ...call.row, lead: leadOf(lead) ? { id: lead.id, name: leadOf(lead).name, isArchived: leadOf(lead).isArchived } : null },
});

// "Not a patient" is offered only to people who manage Call Logs, for a caller we
// don't know, and only when the server's log_outcome options allow it.
export function canFlagNotAPatient(call, { canManageCallLogs = false } = {}) {
    if (!call || !canManageCallLogs || call.lead || !call.number || call.synthetic) return false;
    if (call.callerLabel === 'not_a_patient' || call.callerLabel === 'withheld') return false;
    const allowed = outcomeOptionsFor(call.row);
    return !allowed || allowed.includes('not_a_patient');
}

// Earlier calls from the number follow a link unless it is a shared number, a
// WhatsApp thread, or a live pill call that can't rule a shared number out.
export const includeEarlierCalls = (call) => Boolean(call)
    && !call.isWhatsapp && call.row?.callerLabel !== 'shared_number' && call.row?.linkEarlier !== false;

// Which call-back try a row is on after a No answer (B4 row: follow_up_state), 0 when unknown.
export const tryNumber = (row) => {
    const state = row?.follow_up_state || row?.followUpState || '';
    return { first_attempt: 1, second_attempt: 2, third_attempt: 3 }[state] || (row?.follow_up_result === 'couldnt_reach' ? 3 : 0);
};

// The words the Create lead popup's own "Search existing leads" box starts with for a call: the
// number the way leads store it (national digits, no spaces: the search is trigram based, so a
// spaced or +44 form scores far lower). Not for display.
export const callSearchQuery = (number) => {
    const digits = String(number || '').replace(/[^\d+]/g, '');
    if (!digits) return '';
    if (digits.startsWith('+44')) return `0${digits.slice(3).replace(/^0/, '')}`;
    if (digits.startsWith('0044')) return `0${digits.slice(4).replace(/^0/, '')}`;
    return digits.replace(/^\+/, '');
};

// What the widget's question shows as the caller: the lead's name, else the number as staff say it.
export const callerTitle = (call, format = (v) => v) => call?.lead?.name || (call?.number ? format(call.number) : '') || 'Unknown caller';

// ── Create lead prefill ──────────────────────────────────────────────────────
// Source the wizard offers for a phone enquiry (its own default).
export const CALL_LEAD_SOURCE = 'TELEPHONE';

const whenText = (value) => (value && dayjs(value).isValid() ? dayjs(value).tz(UK).format('ddd DD MMM YYYY, HH:mm') : '');

// First note of the new lead. The line label is always there; the call summary leads when
// there is one, else "Call on <line> at <time>" (the line rung, never the caller).
export function callNote(call, fields) {
    // The server's line can carry an internal connection name ("Simulator"): never in a lead's note.
    const line = call?.line || cleanLineLabel(fields?.line?.value || '');
    const when = whenText(fields?.calledAt?.value || call?.timestamp) || call?.timestampHuman || '';
    if (call?.summary) return [`Call summary: ${call.summary}`, line ? `Line: ${line}` : ''].filter(Boolean).join('\n');
    return ['Call', line ? `on ${line}` : '', when ? `at ${when}` : ''].filter(Boolean).join(' ');
}

// The lead a closing Create lead wizard made for a call, or null. Only what the wizard itself
// saved counts: the explicit id of a finished create, or a `createLead` that was not there when the
// wizard opened (`baselineId`) and is not an existing lead the staff member picked in the form
// (followUpSlice stores that one in the same place). Anything else is never linked to the call.
export function createdLeadIdForCall({ status, id, createLeadId, baselineId = null, existingSelection = null } = {}) {
    if (String(status || '').toLowerCase() === 'finish' && id != null && id !== '') return id;
    if (existingSelection) return null;
    if (createLeadId == null || createLeadId === '' || String(createLeadId) === String(baselineId)) return null;
    return createLeadId;
}

// What the Create lead wizard starts with. `serverPrefill` is `GET calls/{id}/lead-prefill/`
// (null on an older backend); the call fills every gap, so the number is never empty
// when the call had one.
export function buildCallLeadPrefill(call, serverPrefill = null) {
    const fields = withCallFallbacks(prefillFields(serverPrefill), call.row, {
        phone: call.number,
        line: call.line,
        nameIsLine: displayNameIsLine(call.row),
    });
    // UK format (0113…), without spaces: the field saves as typed, and most leads are stored
    // in this national form, which the server's exact-digits duplicate check compares against.
    const phone = String(formatUkPhone(String(fields.phone?.value || call.number || '')) || '').replace(/\s+/g, '');
    const note = callNote(call, fields);
    const personalDetail = {
        ...(fields.firstName?.value ? { first_name: fields.firstName.value } : {}),
        ...(fields.lastName?.value ? { last_name: fields.lastName.value } : {}),
        lead_contact: { ...(phone ? { phone } : {}), ...(note ? { note } : {}) },
        lead_source: { type: CALL_LEAD_SOURCE },
    };
    return { personalDetail, phone, note };
}

// ── Lead suggestions (calls/{id}/lead-suggestions/) ─────────────────────────
export const SUGGESTION_REASON_TEXT = Object.freeze({
    same_number: 'Same number',
    partial_number: 'Similar number',
    archived: 'Archived',
    recent: 'Created recently',
});

const leadName = (l) => str(l.name) || [str(l.first_name), str(l.last_name)].filter(Boolean).join(' ') || `Lead ${l.id}`;

// One lead from the suggestions endpoint, or from the older leads search (first/last name,
// lead_contact.phone).
export function readSuggestedLead(raw) {
    if (!raw || typeof raw !== 'object' || raw.id == null) return null;
    return {
        id: raw.id,
        name: leadName(raw),
        phone: str(raw.phone) || str(raw.lead_contact?.phone),
        email: str(raw.email) || str(raw.lead_contact?.email),
        isArchived: raw.is_archived === true,
        archiveReason: str(raw.archive_reason),
        createdAt: raw.created_at || null,
    };
}

// { suggestions: [{ lead, reasons }], results: [lead] } from either backend.
export function readSuggestions(data) {
    const body = data && typeof data === 'object' ? data : {};
    const suggestions = (Array.isArray(body.suggestions) ? body.suggestions : [])
        .map((entry) => ({ lead: readSuggestedLead(entry?.lead), reasons: (Array.isArray(entry?.reasons) ? entry.reasons : []).filter((r) => SUGGESTION_REASON_TEXT[r]) }))
        .filter((entry) => entry.lead);
    const results = (Array.isArray(body.results) ? body.results : []).map(readSuggestedLead).filter(Boolean);
    return { suggestions, results };
}

export const suggestionsUrl = (practiceId, callId, query = '') => {
    const q = String(query || '').trim();
    return `/api/v1/practices/${practiceId}/calls/${encodeURIComponent(callId)}/lead-suggestions/${q ? `?q=${encodeURIComponent(q)}` : ''}`;
};

// Older backend (404): the plain leads search, archived leads included.
export const fallbackSearchUrl = (practiceId, query) => `/api/v1/practices/${practiceId}/leads/search/?query=${encodeURIComponent(String(query || '').trim())}&include_archived=true`;
